import { useEffect, useState } from "react";
import type { TaskView } from "@common/types/views/tasksView";
import {
    useWorkspaceUserCache,
    WORKSPACE_USER_CACHE_KEYS,
} from "@hooks/useWorkspaceUserCache";
import { useTasksListFiltersCache } from "@hooks/view/useTasksListFiltersCache";
import type { TasksLanding } from "./tasksLanding";
import {
    buildMonthEndCloseView,
    MONTH_END_CLOSE_VIEW_ID,
} from "./views/OrganizationTaskViews";

interface UseWorkspaceTasksViewLinkParams {
    landing: TasksLanding | null;
    /** The drill-down link's `view` param. */
    linkedViewId: string | null | undefined;
}

/**
 * Saves the Month-end close landing a `?view=` link resolved to (see
 * `getOpenClientWorkLink`) over the cached filters, base view and view, so
 * the page content mounts on it rather than on the user's saved state; the
 * page's `closeId` drill-down then sets the Service filter. `true` while the
 * link is still unsaved, so the page keeps its loader up.
 */
export function useWorkspaceTasksViewLink({
    landing,
    linkedViewId,
}: UseWorkspaceTasksViewLinkParams): boolean {
    const [, setCachedFilters] = useTasksListFiltersCache();
    const [, setCachedBaseViewId] = useWorkspaceUserCache<string | null>(
        WORKSPACE_USER_CACHE_KEYS.TASKS_PAGE_BASE_VIEW_ID_CACHE_KEY,
    );
    const [, setCachedView] = useWorkspaceUserCache<TaskView>(
        WORKSPACE_USER_CACHE_KEYS.TASKS_PAGE_VIEW_CACHE_KEY,
    );
    const [hasSavedLink, setHasSavedLink] = useState(false);
    const linkedLanding =
        linkedViewId === MONTH_END_CLOSE_VIEW_ID &&
        landing?.landingViewId === MONTH_END_CLOSE_VIEW_ID
            ? landing
            : null;

    useEffect(() => {
        if (!linkedLanding || hasSavedLink) {
            return;
        }
        setCachedFilters(linkedLanding.defaultQuery);
        setCachedBaseViewId(MONTH_END_CLOSE_VIEW_ID);
        setCachedView(buildMonthEndCloseView());
        setHasSavedLink(true);
    }, [
        linkedLanding,
        hasSavedLink,
        setCachedFilters,
        setCachedBaseViewId,
        setCachedView,
    ]);

    return linkedLanding !== null && !hasSavedLink;
}
